import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export interface MeDeptMembership {
  departmentId: string;
  name: string;
  isHead: boolean;
}

export interface MeLocation {
  id: string;
  name: string;
  address: string;
  radiusM: number;
  lat: number;
  lng: number;
  isPrimary: boolean;
}

export interface MeResponse {
  user: { id: string; email: string; fullName: string; role: "technical_admin" | "super_admin" | "operations" | "hr" | "admin" | "employee" | "client"; attendanceLocationMode: "required" | "open" };
  permissions: string[];
  memberships: { departments: MeDeptMembership[]; projects: unknown[] };
  locations: MeLocation[];
  currentFinancialYear: string;
  selectedFinancialYear: string;
  authVersion: number;
  // Absolute session cap (ISO 8601) anchored at the refresh-token family.
  // Null only for legacy tokens issued before the cap rolled out.
  sessionExpiresAt: string | null;
}

export const meQueryKey = ["auth", "me"] as const;

export function useMe(enabled = true) {
  return useQuery<MeResponse>({
    queryKey: meQueryKey,
    queryFn: () => api.get<MeResponse>("/me").then((r) => r.data),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export interface LoginInput {
  email?: string;
  phone?: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  sessionExpiresAt: string | null;
}

export function useLogin() {
  return useMutation({
    mutationFn: (input: LoginInput) =>
      api.post<LoginResponse>("/auth/login", input).then((r) => r.data),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout").then((r) => r.data),
    onSettled: () => qc.removeQueries({ queryKey: meQueryKey }),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      api.post("/auth/forgot-password", { email }).then((r) => r.data),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: { token: string; newPassword: string }) =>
      api.post("/auth/reset-password", input).then((r) => r.data),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api
        .post("/auth/change-password", {
          current_password: input.currentPassword,
          new_password: input.newPassword,
        })
        .then((r) => r.data),
  });
}

// ---------------------------------------------------------------------------
// OTP — passwordless reset
// ---------------------------------------------------------------------------

export interface OtpRequestResponse {
  channel: "email" | "whatsapp";
  destinationMasked: string;
  expiresAt: string;
  delivered: boolean;
}

export function useRequestOtp() {
  return useMutation<OtpRequestResponse, unknown, { email: string; channel?: "email" | "whatsapp" | null }>({
    mutationFn: (body) =>
      api
        .post<OtpRequestResponse>("/auth/otp/request", body)
        .then((r) => r.data),
  });
}

export function useVerifyOtp() {
  return useMutation<{ token: string; expiresAt: string }, unknown, { email: string; code: string }>({
    mutationFn: (body) =>
      api
        .post<{ token: string; expiresAt: string }>("/auth/otp/verify", body)
        .then((r) => r.data),
  });
}
